import { BatchRawFile, PagedBatchRawFileList } from "@domain/schemas/BI";
import { DataResult, process } from "@progress/kendo-data-query";
import { GridDetailRowProps, GRID_PREVENT_SELECTION_ELEMENT, GridDataStateChangeEvent, getSelectedState, GridSelectionChangeEvent, GridColumn, Grid } from "@progress/kendo-react-grid";
import React, { useEffect, useState } from "react";
import { DATA_ITEM_KEY, SELECTED_FIELD } from "@presentation/components/helpers/constants";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ListJobsService } from "@application/useCases";
import { createDetailDataGridStore, DetailDataGridStore, SampleListItemState } from "@presentation/common";
import { useTranslation } from "react-i18next";

const useStepsGridStore = createDetailDataGridStore<SampleListItemState, BatchRawFile>();

export const SamplesView = (props: GridDetailRowProps) => {
  const batchId: string = props.dataItem.id;
  const listJobsService = useInjection(ListJobsService);

  const { detailList, metaState, selectedState, setDetailList, setMetaStateForDetail, setSelectedStateForDetail } = useStepsGridStore(
    (state: DetailDataGridStore<SampleListItemState, BatchRawFile>) => ({
      detailList: state.detailDictionary[batchId]?.detailList,
      metaState: state.detailDictionary[batchId]?.metaState,
      selectedState: state.detailDictionary[batchId]?.selectedState,
      setDetailList: state.setDetailList,
      setMetaStateForDetail: state.setMetaStateForDetail,
      setSelectedStateForDetail: state.setSelectedStateForDetail,
    }),
  );

  const [dataResult, setDataResult] = useState<DataResult>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { t: loc } = useTranslation();

  const load = async (action: () => Promise<void>) => {
    setIsLoading(true);
    try {
      await action();
    } catch (error) {
      // Handle any errors here
      console.error(loc("errorMessage", { error: error }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchSampleList = async () => {
      const batchSampleListResponse: PagedBatchRawFileList = await listJobsService.getJobBatchSampleListAsync("none", "none", batchId);
      const batchSampleList = batchSampleListResponse.list!.map((sample) => ({
        ...sample,
        fileSize: sample.sizeInBytes ? +(sample.sizeInBytes / 1000000000).toFixed(2) : null,
      }));
      setDetailList(batchId, batchSampleList);
    };
    fetchSampleList();
  }, []);

  useEffect(() => {
    load(async () => {
      if (detailList && metaState) setDataResult(process(detailList, metaState));
    });
  }, [metaState, detailList]);

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setSelectedStateForDetail(batchId, newSelectedState);
  };

  const handleDataStateChange = (e: GridDataStateChangeEvent) => {
    setMetaStateForDetail(batchId, e.dataState);
    setDataResult(process(detailList, e.dataState));
  };

  if (detailList && !isLoading) {
    return (
      <div {...{ [GRID_PREVENT_SELECTION_ELEMENT]: true }} style={{ height: "72vh", maxHeight: "780px", width: "100%" }}>
        <Grid
          style={{ height: "100%", width: "100%" }}
          dataItemKey={DATA_ITEM_KEY}
          selectedField={SELECTED_FIELD}
          scrollable="scrollable"
          resizable={true}
          sortable={true}
          filterable={true}
          navigatable={false}
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
          {...metaState}
          data={dataResult}
          onDataStateChange={handleDataStateChange}
          onSelectionChange={handleSelectionChange}
        >
          <GridColumn field="fileName" title="Raw File Name" width="550" filter="text" />
          <GridColumn field="location" title="Storage Location" width="350" filter="text" />
          <GridColumn field="fileSize" title={loc("fileSizeInGiB")} width="220px" minResizableWidth={220} filter="numeric" />
          <GridColumn width="auto" minResizableWidth={0} filterable={false} />
        </Grid>
      </div>
    );
  }
  return (
    <div style={{ height: "50px", width: "100%" }}>
      <div style={{ position: "absolute", width: "100%" }}>
        <div className="k-loading-image" />
      </div>
    </div>
  );
};
